/* eslint-disable @typescript-eslint/no-use-before-define */
import { Files, FilesKind } from '@flatsy/types/Files'
import { APPLICANT_KIND } from '@flatsy/types/Applications/Applicant'
import React, { createContext, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useApplicantById, useApplicantsSelector } from 'src/hooks/ApplicantsSelector'
import { useApplicationDetails } from 'src/hooks/ApplicationDetails'
import { useUppyUpload } from 'src/hooks/ApplicationUploads'
import { useFiles } from 'src/hooks/UseFiles'
import { RootState } from 'src/redux'
import { selectFilesByApplicant } from 'src/redux/slices/Files/Selector'
import { deleteFile } from 'src/redux/slices/Files/Action'
import { AppDispatch } from 'src/utils/AppDispatch'
import { unwrapResult } from '@reduxjs/toolkit'
import { ApplicationAreaScreen } from './Constant'
import {
  ContextProps,
  InformationFormType,
  ReturnContextProps,
  toggleInformationFormType,
} from './Type'

export const FileContext = createContext<ReturnContextProps>({
  screen: ApplicationAreaScreen.FILE,
  application: undefined,
  asset: undefined,
  files: {
    list: [],
    filesByApplicant: [],
  },
  applicationId: '',
  informationForm: {
    show: {
      open: false,
      id: undefined,
    },
    toggle: () => null,
  },
  Uppy: undefined,
  upload: {
    openModal: false,
    close: () => null,
    open: () => null,
    replace: () => null,
  },
  applicants: {
    current: undefined,
    currentId: undefined,
    list: undefined,
    setCurrent: () => null,
  },
  qualification: undefined,
})

export const FileProvider: React.FC<ContextProps> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch()
  /* -------------------------------------------------------------------------- */
  /*                             APPLICATION DETAILS                            */
  /* -------------------------------------------------------------------------- */
  const { applicationId } = useParams<{ applicationId: string }>()
  const {
    application: { application },
    applicants,
    qualification,
    asset,
  } = useApplicationDetails()

  /* -------------------------------------------------------------------------- */
  /*                                   SCREEN                                   */
  /* -------------------------------------------------------------------------- */
  const [screen, setScreen] = useState<ApplicationAreaScreen>(ApplicationAreaScreen.FILE)

  /* -------------------------------------------------------------------------- */
  /*                              INFORMATION FORM                              */
  /* -------------------------------------------------------------------------- */
  const [informationForm, setInformationForm] = useState<InformationFormType>({
    open: false,
    id: undefined,
  })
  const toggleInformationForm = ({ userId, open }: toggleInformationFormType) => {
    setInformationForm({ open, id: userId })
    setScreen(open ? ApplicationAreaScreen.INFORMATION_FORM : ApplicationAreaScreen.FILE)
  }

  /* -------------------------------------------------------------------------- */
  /*                                  APPLICANT                                 */
  /* -------------------------------------------------------------------------- */
  const main = applicants?.find((applicant) => applicant.kind === APPLICANT_KIND.main)
  const applicantsList = useApplicantsSelector()
  const [currentApplicantId, setCurrentApplicantId] = useState<string | undefined>(
    main?.id || undefined
  )
  const currentApplicant = useApplicantById(currentApplicantId)

  const handleSetCurrentApplicantId = (id: string) => {
    setScreen(ApplicationAreaScreen.FILE)
    setCurrentApplicantId(id)
  }

  /* -------------------------------------------------------------------------- */
  /*                                    FILES                                   */
  /* -------------------------------------------------------------------------- */
  const files = useFiles(applicationId)
  const filesByApplicant = useSelector<RootState, Files[]>((state) =>
    selectFilesByApplicant(state, currentApplicantId)
  )

  /* -------------------------------------------------------------------------- */
  /*                                  UPLOADER                                  */
  /* -------------------------------------------------------------------------- */
  const [openUploadModal, setOpenUploadModal] = useState(false)
  const [preDefinedKind, setPreDefinedKind] = useState<FilesKind | undefined>(undefined)
  const [fileShouldBeDeleted, setFileShouldBeDeleted] = useState<Files | undefined>(undefined)
  const handleOpenUploadModal = () => {
    if (openUploadModal === false) {
      setOpenUploadModal(true)
    }
  }
  const handleReplaceFile = (file?: Files) => {
    if (openUploadModal === false && file) {
      setOpenUploadModal(true)
      setPreDefinedKind(file.kind)
      setFileShouldBeDeleted(file)
    }
  }

  const uppyCallBack = (shouldBeDeleted?: Files) => {
    if (shouldBeDeleted) {
      dispatch(deleteFile({ application: applicationId, file: shouldBeDeleted.id }))
        .then(unwrapResult)
        .then(() => {
          setPreDefinedKind(undefined)
          setFileShouldBeDeleted(undefined)
          handleCloseUploadModal()
        })
        .catch(() => {
          //
        })
    } else {
      handleCloseUploadModal()
    }
  }

  const Uppy = useUppyUpload({
    applicationId,
    id: applicationId,
    height: 160,
    applicantId: currentApplicantId,
    preDefinedKind,
    callBack: () => uppyCallBack(fileShouldBeDeleted),
    callbackAdded: handleOpenUploadModal,
  })

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false)
    Uppy?.resetFiles()
  }
  /* -------------------------------------------------------------------------- */
  /*                                   CONTEXT                                  */
  /* -------------------------------------------------------------------------- */
  const contextValues: ReturnContextProps = {
    screen,
    application,
    asset,
    applicationId,
    files: {
      list: files.files,
      filesByApplicant,
    },
    informationForm: {
      show: informationForm,
      toggle: toggleInformationForm,
    },
    Uppy,
    upload: {
      openModal: openUploadModal,
      close: handleCloseUploadModal,
      open: handleOpenUploadModal,
      replace: handleReplaceFile,
    },
    applicants: {
      currentId: currentApplicantId,
      current: currentApplicant,
      list: applicantsList,
      setCurrent: handleSetCurrentApplicantId,
    },
    qualification,
  }
  return <FileContext.Provider value={contextValues}>{children}</FileContext.Provider>
}

export default FileProvider
